import { useState } from "react";
import { mcpCall, consumerIdOrThrow } from "../lib/mcp-client";

// Cold-start visual onboarding per LS-SPEC-28 §Cold Start.
// Consumer picks 5 style labels per vertical → seeds the visual
// style vector via visual_pref.seed_onboarding.
//
// POC: presents a fixed gallery of style chips (production swaps in
// curated image picker once the media plane is live).

const STYLE_PALETTE: Record<string, string[]> = {
  "travel.hotel": [
    "modern_minimal", "boutique_japanese", "luxe_resort",
    "ryokan_traditional", "city_design", "wellness_retreat",
    "beach_resort", "mountain_lodge",
  ],
  food: [
    "kaiseki", "izakaya", "wine_bar_modern", "sushi_counter",
    "street_food", "tasting_menu", "comfort_japanese", "fusion",
  ],
  fashion: [
    "streetwear", "japanese_minimal", "smart_casual", "vintage",
    "luxury_workwear", "artisan_handmade", "athletic", "avant_garde",
  ],
  wellness: [
    "onsen_traditional", "modern_spa", "yoga_studio", "boxing_gym",
    "running_club", "meditation_retreat", "physio_clinical",
  ],
};


export function ColdStartOnboarding() {
  const [vertical, setVertical] = useState<keyof typeof STYLE_PALETTE>(
    "travel.hotel",
  );
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [seeded, setSeeded] = useState<string[]>([]);
  const [err, setErr] = useState<string | null>(null);

  const toggle = (label: string) => {
    setPicked((cur) => {
      const next = new Set(cur);
      if (next.has(label)) next.delete(label);
      else if (next.size < 5) next.add(label);
      return next;
    });
  };

  const submit = async () => {
    setErr(null);
    try {
      await mcpCall("visual_pref.seed_onboarding", {
        consumer_id: consumerIdOrThrow(),
        vertical, style_labels: Array.from(picked),
      });
      setSeeded((prev) => Array.from(new Set([...prev, vertical])));
      setPicked(new Set());
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  return (
    <div className="flex flex-col gap-3 px-4 py-3">
      <header>
        <h1 className="text-base font-medium text-(--color-ink)">
          Show me your style
        </h1>
        <p className="text-xs text-(--color-ink-muted)">
          Pick up to 5 styles you'd actually choose. I use these to
          shortlist for you — you can edit any time in Settings.
        </p>
      </header>

      <div className="flex flex-wrap gap-1.5 text-xs">
        {Object.keys(STYLE_PALETTE).map((v) => (
          <button
            key={v}
            onClick={() => { setVertical(v as keyof typeof STYLE_PALETTE); setPicked(new Set()); }}
            className={[
              "rounded-(--radius-pill) px-2.5 py-1",
              vertical === v
                ? "bg-(--color-accent-gold) text-stone-900"
                : seeded.includes(v)
                ? "bg-(--color-canvas-card) text-(--color-ink-muted)"
                : "bg-(--color-canvas-card) text-(--color-ink-faint) hover:text-(--color-ink)",
            ].join(" ")}
          >
            {v}{seeded.includes(v) ? " ✓" : ""}
          </button>
        ))}
      </div>

      <section className="rounded-xl border border-white/5 bg-(--color-canvas-card) p-3">
        <div className="grid grid-cols-2 gap-1.5">
          {(STYLE_PALETTE[vertical] ?? []).map((label) => {
            const on = picked.has(label);
            return (
              <button
                key={label}
                onClick={() => toggle(label)}
                className={[
                  "rounded-md px-2 py-2 text-left text-xs transition",
                  on ? "bg-(--color-accent-gold) text-stone-900 font-medium"
                     : "bg-(--color-canvas-elev) text-(--color-ink-muted) hover:text-(--color-ink)",
                ].join(" ")}
              >
                {on ? "✓ " : ""}{label.replace(/_/g, " ")}
              </button>
            );
          })}
        </div>
        <p className="mt-2 text-xs text-(--color-ink-faint)">
          {picked.size} of 5 picked for {vertical}
        </p>
        {err && (
          <p className="mt-2 rounded bg-red-900/40 px-2 py-1 text-xs text-red-200">
            {err}
          </p>
        )}
        <button
          onClick={submit}
          disabled={picked.size === 0}
          className="mt-3 w-full rounded-(--radius-pill) bg-(--color-accent-gold) px-3 py-2 text-sm font-medium text-stone-900 disabled:opacity-40"
        >
          Save {picked.size} {picked.size === 1 ? "style" : "styles"} for {vertical}
        </button>
      </section>
    </div>
  );
}
